import React, { useState, useRef, useEffect } from "react";
import { uploadFile } from "@/services/firebase/storage";
import { validateImageFile } from "@/utils/storageUtils";

interface ImageUploadProps {
  value?: string;
  onChange: (url: string) => void;
  storagePath?: string;
  maxSizeMB?: number;
  label?: string;
  required?: boolean;
  error?: string;
}

export default function ImageUpload({
  value,
  onChange,
  storagePath = "drama_covers",
  maxSizeMB = 5,
  label = "封面圖片",
  required = false,
  error,
}: ImageUploadProps) {
  const [preview, setPreview] = useState<string>(value || "");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 同步外部 value 到 preview
  useEffect(() => {
    // 只有在非上傳狀態時才同步外部 value
    // 上傳過程中保持本地預覽，上傳完成後由 handleFileSelect 更新
    if (!uploading) {
      if (value) {
        // 如果是完整的 URL（http:// 或 https://），直接使用
        if (value.startsWith("http://") || value.startsWith("https://")) {
          setPreview(value);
        } else {
          // 如果是 Storage 路徑（例如 "drama_covers/xxx.jpg"），獲取下載 URL 用於預覽
          import("@/services/firebase/storage")
            .then(({ getFileDownloadURL }) => getFileDownloadURL(value))
            .then((url) => setPreview(url))
            .catch((error) => {
              console.warn("無法獲取預覽 URL:", error);
              // 如果獲取失敗，清空預覽
              setPreview("");
            });
        }
      } else {
        setPreview("");
      }
    }
  }, [value, uploading]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 驗證文件
    const validation = validateImageFile(file, maxSizeMB);
    if (!validation.valid) {
      setUploadError(validation.error || "圖片驗證失敗");
      return;
    }

    setUploadError(null);
    setUploading(true);

    try {
      // 創建預覽
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      // 上傳文件
      const fileName = `${Date.now()}_${file.name}`;
      const path = `${storagePath}/${fileName}`;
      await uploadFile(path, file);

      // 存完整路徑到 Firestore（例如 "drama_covers/1764908647930_poster_05.jpg"）
      // App 會直接使用這個路徑從 Firebase Storage 獲取下載 URL
      const storagePathValue = path;
      
      // 為了預覽，需要獲取下載 URL
      try {
        const { getFileDownloadURL } = await import("@/services/firebase/storage");
        const downloadURL = await getFileDownloadURL(storagePathValue);
        setPreview(downloadURL);
      } catch (previewError) {
        console.warn("無法獲取預覽 URL，但上傳成功:", previewError);
        // 即使預覽失敗，也繼續（因為檔案已上傳）
      }
      
      // 傳完整路徑給父組件（例如 "drama_covers/1764908647930_poster_05.jpg"）
      onChange(storagePathValue);
      setUploading(false);
    } catch (err: any) {
      console.error("Error uploading image:", err);
      setUploadError(err.message || "上傳失敗");
      setUploading(false);
      // 上傳失敗時，清除預覽
      setPreview("");
    }
  };

  const handleRemove = () => {
    setPreview("");
    onChange("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-surface-700 dark:text-surface-300">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className="flex items-start gap-4">
        {/* Preview */}
        <div className="relative w-32 h-48 border border-surface-300 dark:border-surface-600 rounded-lg overflow-hidden bg-surface-100 dark:bg-surface-700">
          {preview ? (
            <>
              <img
                src={preview}
                alt="預覽"
                className="w-full h-full object-cover"
              />
              {!uploading && (
                <button
                  type="button"
                  onClick={handleRemove}
                  className="absolute top-1 right-1 p-1 bg-destructive text-destructive-foreground rounded-full hover:bg-destructive/90 transition-colors"
                  aria-label="移除圖片"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-surface-400">
              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
          )}

          {uploading && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
          )}
        </div>

        {/* Upload Button */}
        <div className="flex-1">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            disabled={uploading}
            className="hidden"
            id="image-upload-input"
          />
          <label
            htmlFor="image-upload-input"
            className={`inline-flex items-center px-4 py-2 border rounded-lg cursor-pointer transition-colors ${
              uploading
                ? "bg-surface-300 dark:bg-surface-600 text-surface-500 cursor-not-allowed"
                : "bg-white dark:bg-surface-700 border-surface-300 dark:border-surface-600 text-surface-700 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-600"
            }`}
          >
            {uploading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                上傳中...
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                選擇圖片
              </>
            )}
          </label>
          <p className="mt-2 text-xs text-surface-500 dark:text-surface-400">
            支援 JPG、PNG、GIF，最大 {maxSizeMB}MB
          </p>
        </div>
      </div>

      {/* Error Message */}
      {(error || uploadError) && (
        <p className="text-sm text-red-500">{error || uploadError}</p>
      )}
    </div>
  );
}

